import { useState, useEffect } from 'react';
import { licenseCodeApi, licensePlanApi } from '../services/api';
import { showError } from '../utils/showError';
import { extractErrorMessage } from '../utils/errorUtils';
import {
  PageHeader,
  SearchBar,
  LoadingSpinner,
  EmptyState,
  ListContainer,
  Pagination,
  ErrorMessage,
  SuccessMessage,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Modal,
  FormField,
  FormSelect,
  FormTextarea,
  FormCheckbox,
  ConfirmationModal,
  Badge,
} from '../components/common';
import { BookOpen, Plus, Edit2, Trash2, Copy, Check } from 'lucide-react';
import { formatLicenseCode, formatRedemptions } from '../constants/licenses';

const LicenseCodes = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [searchString, setSearchString] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(10);

  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    licensePlanId: '',
    maxRedemptions: '',
    notes: '',
  });
  const [createErrors, setCreateErrors] = useState({});
  const [creating, setCreating] = useState(false);

  // Created code display
  const [createdCode, setCreatedCode] = useState(null);

  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [editForm, setEditForm] = useState({
    maxRedemptions: '',
    notes: '',
    isActive: true,
  });
  const [editSaving, setEditSaving] = useState(false);

  // Delete modal
  const [deleteModal, setDeleteModal] = useState({ show: false, codeId: null, code: '' });

  // Clipboard
  const [copiedId, setCopiedId] = useState(null);

  // Plan dropdown
  const [planOptions, setPlanOptions] = useState([]);

  // Fetch plans on mount
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await licensePlanApi.list({ pageSize: 100 });
        const items = response.data.items || response.data || [];
        setPlanOptions(items.map((p) => ({ value: p.licensePlanId, label: `${p.name} (${p.planCode})` })));
      } catch (err) {
        console.error('Error fetching plans:', err);
      }
    };
    fetchPlans();
  }, []);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchString(searchInput);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchCodes = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        pageNumber: currentPage,
        pageSize,
        searchString: searchString || undefined,
      };
      const response = await licenseCodeApi.list(params);
      setCodes(response.data.items || []);
      setTotalPages(response.data.totalPages || 1);
      setTotalCount(response.data.totalCount || 0);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCodes();
  }, [currentPage, searchString]);

  const handleSearchChange = (value) => {
    setSearchInput(value);
  };

  // Create modal
  const openCreateModal = () => {
    setCreateForm({ licensePlanId: '', maxRedemptions: '', notes: '' });
    setCreateErrors({});
    setCreatedCode(null);
    setShowCreateModal(true);
  };

  const validateCreateForm = () => {
    const errors = {};
    if (!createForm.licensePlanId) errors.licensePlanId = 'Plan is required';
    setCreateErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreate = async () => {
    if (!validateCreateForm()) return;
    setCreating(true);
    try {
      const payload = {
        licensePlanId: createForm.licensePlanId,
        maxRedemptions: createForm.maxRedemptions !== '' ? parseInt(createForm.maxRedemptions) : null,
        notes: createForm.notes.trim() || null,
      };
      const response = await licenseCodeApi.create(payload);
      const newCode = response.data?.code || response.data?.licenseCode || response.data;
      setCreatedCode(typeof newCode === 'string' ? newCode : newCode?.code || JSON.stringify(newCode));
      setSuccessMessage('License code generated successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      await fetchCodes();
    } catch (err) {
      showError(err);
    } finally {
      setCreating(false);
    }
  };

  // Edit modal
  const openEditModal = (codeItem) => {
    setEditingCode(codeItem);
    setEditForm({
      maxRedemptions: codeItem.maxRedemptions != null ? codeItem.maxRedemptions : '',
      notes: codeItem.notes || '',
      isActive: codeItem.isActive ?? true,
    });
    setShowEditModal(true);
  };

  const handleEdit = async () => {
    setEditSaving(true);
    try {
      const payload = {
        maxRedemptions: editForm.maxRedemptions !== '' ? parseInt(editForm.maxRedemptions) : null,
        notes: editForm.notes.trim() || null,
        isActive: editForm.isActive,
      };
      await licenseCodeApi.update(editingCode.licenseCodeId, payload);
      setShowEditModal(false);
      setSuccessMessage('License code updated successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      await fetchCodes();
    } catch (err) {
      showError(err);
    } finally {
      setEditSaving(false);
    }
  };

  // Delete
  const handleDelete = async () => {
    try {
      await licenseCodeApi.delete(deleteModal.codeId);
      setDeleteModal({ show: false, codeId: null, code: '' });
      setSuccessMessage('License code deleted successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      await fetchCodes();
    } catch (err) {
      showError(err);
    }
  };

  // Clipboard copy
  const handleCopy = async (code, id) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = code;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading && codes.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="License Codes"
        subtitle={`Manage redeemable license codes (${totalCount} total)`}
        icon={BookOpen}
        action={
          <Button variant="primary" onClick={openCreateModal}>
            <Plus size={16} className="mr-2" />
            Generate Code
          </Button>
        }
      />

      {error && <ErrorMessage message={error} onDismiss={() => setError(null)} />}
      {successMessage && <SuccessMessage message={successMessage} onDismiss={() => setSuccessMessage(null)} />}

      <div className="mb-6">
        <SearchBar
          value={searchInput}
          onChange={handleSearchChange}
          placeholder="Search by code or notes..."
        />
      </div>

      <ListContainer>
        {codes.length === 0 ? (
          <EmptyState
            icon={BookOpen}
            title="No license codes found"
            description={searchInput ? 'Try adjusting your search' : 'Get started by generating your first license code'}
            action={
              <Button variant="primary" onClick={openCreateModal}>
                <Plus size={16} className="mr-2" />
                Generate Code
              </Button>
            }
          />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableCell header>Code</TableCell>
                  <TableCell header>Plan</TableCell>
                  <TableCell header>Redemptions</TableCell>
                  <TableCell header>Active</TableCell>
                  <TableCell header>Created</TableCell>
                  <TableCell header>Notes</TableCell>
                  <TableCell header>Actions</TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {codes.map((codeItem) => (
                  <TableRow key={codeItem.licenseCodeId}>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <span className="font-mono text-sm text-primary-600 font-medium">
                          {formatLicenseCode(codeItem.code)}
                        </span>
                        <button
                          onClick={() => handleCopy(codeItem.code, codeItem.licenseCodeId)}
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                          title="Copy to clipboard"
                        >
                          {copiedId === codeItem.licenseCodeId ? (
                            <Check size={14} className="text-green-500" />
                          ) : (
                            <Copy size={14} />
                          )}
                        </button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm font-medium text-gray-900">
                        {codeItem.planName || codeItem.licensePlanName || 'N/A'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-gray-700">
                        {formatRedemptions(codeItem.currentRedemptions || 0, codeItem.maxRedemptions)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={codeItem.isActive ? 'success' : 'secondary'} size="small">
                        {codeItem.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-gray-600">{formatDate(codeItem.createdAt)}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-gray-600 truncate max-w-[150px] block">
                        {codeItem.notes || '-'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => openEditModal(codeItem)}
                          className="text-gray-600 hover:text-primary-600 transition-colors"
                          title="Edit"
                        >
                          <Edit2 size={16} />
                        </button>
                        {(codeItem.currentRedemptions || 0) === 0 && (
                          <button
                            onClick={() =>
                              setDeleteModal({
                                show: true,
                                codeId: codeItem.licenseCodeId,
                                code: formatLicenseCode(codeItem.code),
                              })
                            }
                            className="text-gray-600 hover:text-red-600 transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {totalPages > 1 && (
              <div className="mt-6">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                  totalItems={totalCount}
                  itemsPerPage={pageSize}
                />
              </div>
            )}
          </>
        )}
      </ListContainer>

      {/* Generate Code Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Generate License Code"
        size="medium"
      >
        <div className="space-y-4">
          {createdCode ? (
            <>
              <div className="text-center py-4">
                <p className="text-sm text-gray-600 mb-3">Your new license code has been generated:</p>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-center justify-center space-x-3">
                  <span className="font-mono text-xl font-bold text-primary-600 tracking-wider">
                    {formatLicenseCode(createdCode)}
                  </span>
                  <button
                    onClick={() => handleCopy(createdCode, 'created')}
                    className="text-gray-500 hover:text-primary-600 transition-colors p-1"
                    title="Copy to clipboard"
                  >
                    {copiedId === 'created' ? (
                      <Check size={20} className="text-green-500" />
                    ) : (
                      <Copy size={20} />
                    )}
                  </button>
                </div>
                {copiedId === 'created' && (
                  <p className="text-sm text-green-600 mt-2">Copied to clipboard</p>
                )}
              </div>
              <div className="flex justify-end pt-4 border-t border-gray-200">
                <Button variant="primary" onClick={() => setShowCreateModal(false)}>
                  Done
                </Button>
              </div>
            </>
          ) : (
            <>
              <FormSelect
                label="License Plan"
                name="licensePlanId"
                value={createForm.licensePlanId}
                onChange={(e) => setCreateForm({ ...createForm, licensePlanId: e.target.value })}
                required
                error={createErrors.licensePlanId}
                options={[
                  { value: '', label: 'Select a plan...' },
                  ...planOptions,
                ]}
              />

              <FormField
                label="Max Redemptions"
                name="maxRedemptions"
                type="number"
                value={createForm.maxRedemptions}
                onChange={(e) => setCreateForm({ ...createForm, maxRedemptions: e.target.value })}
                placeholder="Unlimited"
                helpText="Leave empty for unlimited redemptions"
                min="1"
              />

              <FormTextarea
                label="Notes"
                name="notes"
                value={createForm.notes}
                onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                rows={2}
                placeholder="Optional notes about this code..."
              />

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <Button variant="secondary" onClick={() => setShowCreateModal(false)} disabled={creating}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleCreate} loading={creating}>
                  Generate Code
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Edit Code Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit License Code"
        size="medium"
      >
        <div className="space-y-4">
          {editingCode && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-2">
              <p className="text-xs text-gray-500 mb-1">Code</p>
              <span className="font-mono text-sm font-bold text-gray-900">
                {formatLicenseCode(editingCode.code)}
              </span>
            </div>
          )}

          <FormField
            label="Max Redemptions"
            name="maxRedemptions"
            type="number"
            value={editForm.maxRedemptions}
            onChange={(e) => setEditForm({ ...editForm, maxRedemptions: e.target.value })}
            placeholder="Unlimited"
            helpText="Leave empty for unlimited redemptions"
            min="1"
          />

          <FormTextarea
            label="Notes"
            name="notes"
            value={editForm.notes}
            onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
            rows={2}
            placeholder="Optional notes..."
          />

          <FormCheckbox
            label="Active"
            checked={editForm.isActive}
            onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
            helpText="Inactive codes cannot be redeemed"
          />

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <Button variant="secondary" onClick={() => setShowEditModal(false)} disabled={editSaving}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleEdit} loading={editSaving}>
              Update Code
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.show}
        onClose={() => setDeleteModal({ show: false, codeId: null, code: '' })}
        onConfirm={handleDelete}
        title="Delete License Code"
        message={`Are you sure you want to delete code ${deleteModal.code}? This action cannot be undone.`}
        confirmText="Delete"
        confirmVariant="danger"
      />
    </div>
  );
};

export default LicenseCodes;
